<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { iconClass, placeIconLabels, placeIcons } from '../lib/utils/icons';
  import { onMount, onDestroy, untrack } from 'svelte';
  import { navigate, Link } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { MapInfo, Member, Place } from '../lib/api/types';
  import { groups } from '../lib/stores/groups';
  import { groupLabel } from '../lib/utils/groupLabel';
  import { isAuthenticated } from '../lib/stores/auth';
  import { websocket } from '../lib/stores/websocket';
  import { confirm } from '../lib/stores/confirmDialog';
  import { circlePolygon, distanceM, eastOf } from '../lib/utils/geo';
  import { PlaceLayer, placeColor } from '../lib/components/places/placeLayer';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import PlaceHistory from '../lib/components/places/PlaceHistory.svelte';
  import PlaceSearch, { type Found } from '../lib/components/places/PlaceSearch.svelte';
  import { integer, t } from '../lib/i18n/index.ts';

  // Draft is the place being created or edited; id is absent for a new one.
  interface Draft {
    id?: string;
    name: string;
    lat: number;
    lon: number;
    radius_m: number;
    icon?: string;
  }

  const MIN_RADIUS = 50;
  const MAX_RADIUS = 2000;
  const DEFAULT_RADIUS = 150;
  // SLOT stands in for the link inside placesNoGroup, which is split around it.
  const SLOT = '\u0001';

  const selected = groups.selected;

  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let layer: PlaceLayer | null = null;
  let stopTheme: (() => void) | null = null;
  let mapReady = $state(false);
  let places: Place[] = $state([]);
  let members: Member[] = $state([]);
  let error = $state('');
  let placing = $state(false);
  let draft = $state<Draft | null>(null);
  let saving = $state(false);
  let historyKey = $state(0);
  let centerMarker: maplibregl.Marker | null = null;
  let handleMarker: maplibregl.Marker | null = null;
  let unsubscribeTopic: (() => void) | null = null;
  let fitted = false;

  let editing = $derived(draft?.id ? places.find((p) => p.id === draft!.id) : undefined);

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  onMount(async () => {
    try {
      await groups.load();
    } catch (e: any) {
      error = e.message;
    }
    let info: MapInfo | null = null;
    try {
      info = await api.get<MapInfo>('/map');
    } catch (e: any) {
      error = e.message;
    }
    if (!mapEl) return;
    map = northUpMap({
      container: mapEl,
      style: styleFor(info),
      center: [-98.5, 39.8],
      zoom: 3,
      attributionControl: false,
    });
    addCredits(map, info, 'other');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    layer = new PlaceLayer(map, (id) => {
      if (!placing && !draft) select(id);
    });
    map.on('click', (e) => {
      if (!placing) return;
      placing = false;
      map!.getCanvas().style.cursor = '';
      edit({ name: '', lat: e.lngLat.lat, lon: e.lngLat.lng, radius_m: DEFAULT_RADIUS });
    });
    stopTheme = followTheme(map, info, () => {
      layer!.attach();
      const color = placeColor();
      map!.addSource('draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'draft-fill', type: 'fill', source: 'draft', paint: { 'fill-color': color, 'fill-opacity': 0.25 } });
      map!.addLayer({ id: 'draft-line', type: 'line', source: 'draft', paint: { 'line-color': color, 'line-width': 2, 'line-dasharray': [2, 1] } });
      mapReady = true;
      render();
    });
  });

  onDestroy(() => {
    stopTheme?.();
    unsubscribeTopic?.();
    map?.remove();
  });

  // Reload everything when the group changes, and follow its changes live.
  $effect(() => {
    const id = $selected;
    untrack(() => follow(id));
  });

  function follow(id: string | null) {
    unsubscribeTopic?.();
    unsubscribeTopic = null;
    places = [];
    members = [];
    closeEditor();
    fitted = false;
    if (!id) return;
    loadPlaces(id);
    api.get<Member[]>(`/groups/${id}/members`).then((list) => (members = list)).catch((e: any) => (error = e.message));
    const stopTopic = websocket.subscribeTopic(`group:${id}`, (_payload, topic, type) => {
      if (topic !== `group:${id}`) return;
      if (type === 'places') loadPlaces(id);
      else if (type === 'event' || type === 'trip_end') historyKey++;
      else if (type === 'members') api.get<Member[]>(`/groups/${id}/members`).then((list) => {
        if (id === $selected) members = list;
      }).catch(() => {});
    });
    // Changes sent while the socket was down are lost, so everything the
    // topic keeps current is read again (001 FR-113).
    const stopReconnect = websocket.onReconnect(() => {
      loadPlaces(id);
      historyKey++;
      api.get<Member[]>(`/groups/${id}/members`).then((list) => {
        if (id === $selected) members = list;
      }).catch(() => {});
    });
    unsubscribeTopic = () => {
      stopTopic();
      stopReconnect();
    };
  }

  async function loadPlaces(groupId: string) {
    try {
      places = await api.get<Place[]>(`/groups/${groupId}/places`);
      render();
    } catch (e: any) {
      error = e.message;
    }
  }

  function render() {
    if (!map || !mapReady || !layer) return;
    layer.set(places, draft?.id ?? null);
    const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
    if (draft) data.features.push({ type: 'Feature', properties: {}, geometry: circlePolygon(draft.lat, draft.lon, draft.radius_m) });
    (map.getSource('draft') as maplibregl.GeoJSONSource | undefined)?.setData(data);
    if (!fitted && places.length > 0) {
      fitted = true;
      const bounds = new maplibregl.LngLatBounds();
      for (const p of places) bounds.extend([p.lon, p.lat]);
      map.fitBounds(bounds, { padding: 120, maxZoom: 15, duration: 0 });
    }
  }

  function select(id: string) {
    const p = places.find((x) => x.id === id);
    if (!p) return;
    edit({ id: p.id, name: p.name, lat: p.lat, lon: p.lon, radius_m: p.radius_m, icon: p.icon });
    map?.flyTo({ center: [p.lon, p.lat], zoom: Math.max(map.getZoom(), 15) });
  }

  // edit opens the editor on a draft with draggable center and radius handles.
  function edit(d: Draft) {
    closeEditor();
    draft = d;
    error = '';
    if (!map) return;
    const center = document.createElement('div');
    center.className = 'place-handle place-handle-center';
    center.title = t('placesDragMove');
    centerMarker = new maplibregl.Marker({ element: center, draggable: true }).setLngLat([d.lon, d.lat]).addTo(map);
    centerMarker.on('drag', () => {
      if (!draft || !centerMarker) return;
      const at = centerMarker.getLngLat();
      draft.lat = at.lat;
      draft.lon = at.lng;
      placeHandle();
      render();
    });
    const handle = document.createElement('div');
    handle.className = 'place-handle';
    handle.title = t('placesDragResize');
    handleMarker = new maplibregl.Marker({ element: handle, draggable: true }).setLngLat([eastOf(d.lat, d.lon, d.radius_m), d.lat]).addTo(map);
    handleMarker.on('drag', () => {
      if (!draft || !handleMarker) return;
      const at = handleMarker.getLngLat();
      draft.radius_m = clampRadius(distanceM(draft.lat, draft.lon, at.lat, at.lng));
      render();
    });
    handleMarker.on('dragend', placeHandle);
    render();
  }

  // placeHandle puts the radius handle back on the circle's east edge.
  function placeHandle() {
    if (draft && handleMarker) handleMarker.setLngLat([eastOf(draft.lat, draft.lon, draft.radius_m), draft.lat]);
  }

  function clampRadius(m: number): number {
    return Math.round(Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, m)));
  }

  function setRadius(value: number) {
    if (!draft) return;
    draft.radius_m = clampRadius(value || MIN_RADIUS);
    placeHandle();
    render();
  }

  function closeEditor() {
    centerMarker?.remove();
    handleMarker?.remove();
    centerMarker = null;
    handleMarker = null;
    draft = null;
    render();
  }

  function startPlacing() {
    closeEditor();
    placing = true;
    if (map) map.getCanvas().style.cursor = 'crosshair';
  }

  // mapCenter is where the map shows, which a search favors.
  function mapCenter(): { lat: number; lon: number } | null {
    const c = map?.getCenter();
    return c ? { lat: c.lat, lon: c.lng } : null;
  }

  // placeFound starts a new place at a search result, named after it.
  function placeFound(f: Found) {
    cancelPlacing();
    edit({ name: f.name.slice(0, 64), lat: f.lat, lon: f.lon, radius_m: DEFAULT_RADIUS });
    map?.flyTo({ center: [f.lon, f.lat], zoom: Math.max(map.getZoom(), 16) });
  }

  // moveToFound moves the place being edited to a search result, naming it
  // after the result only when it has no name yet.
  function moveToFound(f: Found) {
    if (!draft) return;
    draft.lat = f.lat;
    draft.lon = f.lon;
    if (!draft.name.trim()) draft.name = f.name.slice(0, 64);
    centerMarker?.setLngLat([f.lon, f.lat]);
    placeHandle();
    render();
    map?.flyTo({ center: [f.lon, f.lat], zoom: Math.max(map.getZoom(), 16) });
  }

  function cancelPlacing() {
    placing = false;
    if (map) map.getCanvas().style.cursor = '';
  }

  async function save() {
    const groupId = $selected;
    if (!draft || !groupId) return;
    const body = { name: draft.name.trim(), lat: draft.lat, lon: draft.lon, radius_m: draft.radius_m, icon: draft.icon };
    if (!body.name) {
      error = t('placesNameRequired');
      return;
    }
    saving = true;
    error = '';
    try {
      if (draft.id) await api.patch<Place>(`/groups/${groupId}/places/${draft.id}`, body);
      else await api.post<Place>(`/groups/${groupId}/places`, body);
      closeEditor();
      await loadPlaces(groupId);
    } catch (e: any) {
      error = e.message;
    } finally {
      saving = false;
    }
  }

  async function remove() {
    const groupId = $selected;
    if (!draft?.id || !groupId) return;
    const question = editing ? t('placesDeleteConfirm', { place: editing.name }) : t('placesDeleteThisConfirm');
    if (!(await confirm(question))) return;
    try {
      await api.delete(`/groups/${groupId}/places/${draft.id}`);
      closeEditor();
      await loadPlaces(groupId);
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

<!-- On a narrow screen, or zoomed in, the map sits above the list instead of beside it. -->
<div class="flex flex-col-reverse md:flex-row flex-1 min-h-0">
  <aside class="w-full md:w-96 h-1/2 md:h-auto shrink-0 border-t md:border-t-0 md:border-r flex flex-col min-h-0 overflow-y-auto" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <div class="p-4 border-b" style="border-color: var(--theme-surface0);">
      {#if $groups.length > 0}
        <label for="group" class="block text-xs text-text-muted mb-1">{t('placesGroup')}</label>
        <select id="group" class="input w-full" bind:value={$selected}>
          {#each $groups as c (c.id)}
            <option value={c.id}>{groupLabel(c, $groups)}</option>
          {/each}
        </select>
      {:else}
        {@const parts = t('placesNoGroup', { create: SLOT }).split(SLOT)}
        <p class="text-sm text-text-muted">{parts[0]}<Link to="/groups">{t('placesNoGroupCreate')}</Link>{parts[1] ?? ''}</p>
      {/if}
      {#if error}
        <p class="text-sm mt-2" style="color: var(--theme-error);">{error}</p>
      {/if}
    </div>

    {#if draft}
      <div class="p-4 space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold">{draft.id ? t('placesEdit') : t('placesNew')}</h2>
          <button class="text-text-muted hover:opacity-80" aria-label={t('appClose')} onclick={closeEditor}><i aria-hidden="true" class="las la-times"></i></button>
        </div>
        <div>
          <span class="block text-xs text-text-muted mb-1">{t('placesWhere')}</span>
          <PlaceSearch near={mapCenter} onPick={moveToFound} />
        </div>
        <div>
          <label for="place-name" class="block text-xs text-text-muted mb-1">{t('placesName')}</label>
          <input id="place-name" class="input w-full" maxlength="64" placeholder={t('placesNameHint')} bind:value={draft.name}
                 onkeydown={(e) => e.key === 'Enter' && save()} />
        </div>
        <div>
          <span class="block text-xs text-text-muted mb-1">{t('placesIcon')}</span>
          <!-- A new place without a chosen icon gets home or a pin by its name (010 FR-024). -->
          <div class="flex flex-wrap gap-1" role="radiogroup" aria-label={t('placesIcon')}>
            {#each placeIcons as icon (icon)}
              {@const chosen = icon === (draft.icon ?? (draft.name.trim().toLowerCase() === 'home' ? 'home' : 'pin'))}
              <button type="button" role="radio" aria-checked={chosen} aria-label={placeIconLabels[icon]} title={placeIconLabels[icon]}
                      class="icon-choice" class:chosen onclick={() => { if (draft) draft.icon = icon; }}>
                <i aria-hidden="true" class={iconClass(icon)}></i>
              </button>
            {/each}
          </div>
        </div>
        <div>
          <label for="place-radius" class="block text-xs text-text-muted mb-1">{t('placesRadius', { radius: integer(draft.radius_m) })}</label>
          <input id="place-radius" type="range" class="w-full" min={MIN_RADIUS} max={MAX_RADIUS} step="10"
                 value={draft.radius_m} oninput={(e) => setRadius(+e.currentTarget.value)} />
          <p class="text-xs text-text-muted mt-1">{t('placesDragHint')}</p>
        </div>
        <div class="flex gap-2">
          <button class="btn btn-primary" disabled={saving} onclick={save}>{draft.id ? t('appSave') : t('placesCreate')}</button>
          <button class="btn btn-secondary" onclick={closeEditor}>{t('appCancel')}</button>
          {#if draft.id}
            <button class="btn btn-danger ml-auto" onclick={remove}>{t('appDelete')}</button>
          {/if}
        </div>

        {#if draft.id && $selected}
          <section class="pt-2 border-t space-y-2" style="border-color: var(--theme-surface0);">
            <h3 class="text-sm font-semibold">{t('placesHistory')}</h3>
            <PlaceHistory groupId={$selected} placeId={draft.id} {members} refresh={historyKey} />
          </section>
        {/if}
      </div>
    {:else if $selected}
      <div class="p-4 space-y-3">
        {#if placing}
          <div class="flex items-center justify-between text-sm">
            <span>{t('placesPickHint')}</span>
            <button class="btn btn-secondary btn-sm" onclick={cancelPlacing}>{t('appCancel')}</button>
          </div>
          <PlaceSearch near={mapCenter} onPick={placeFound} />
        {:else}
          <button class="btn btn-primary w-full" onclick={startPlacing}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('placesNew')}</button>
        {/if}
        <ul>
          {#each places as p (p.id)}
            <li>
              <button class="w-full text-left py-2 flex items-center gap-2 hover:opacity-80" onclick={() => select(p.id)}>
                <i aria-hidden="true" class="{iconClass(p.icon)} text-lg" style="color: var(--theme-success);" aria-label={placeIconLabels[p.icon]}></i>
                <span class="font-medium truncate">{p.name}</span>
                <span class="text-xs text-text-muted shrink-0 ml-auto">{t('placesMeters', { radius: integer(p.radius_m) })}</span>
              </button>
            </li>
          {:else}
            <li class="text-sm text-text-muted">{t('placesNone')}</li>
          {/each}
        </ul>
      </div>
    {/if}
  </aside>

  <div class="flex-1 relative min-w-0">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

<style>
  .icon-choice {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    background: var(--theme-surface0);
    color: var(--theme-subtext0);
  }
  .icon-choice.chosen {
    background: var(--theme-accent);
    color: var(--theme-base);
  }
  :global(.place-handle) {
    width: 1rem;
    height: 1rem;
    border-radius: 9999px;
    background: var(--theme-base);
    border: 3px solid var(--theme-green);
    box-shadow: var(--theme-shadow-md);
    cursor: ew-resize;
  }
  :global(.place-handle-center) {
    width: 1.25rem;
    height: 1.25rem;
    background: var(--theme-green);
    border-color: var(--theme-base);
    cursor: move;
  }
</style>
